import { Outlet, useLoaderData } from "react-router-dom";
import {
  getRenewedUserInfo, formatDate, formatDatetime, removeMeetingById, trackEvent, mapObject, groupBy,
  getUploadedTranscriptsRequest, getTranscriptionAsText, getContextStrings, getContextStringsFromList,
  plural, mergeObjectsByMeetingId, highlightText, countWordSetMatches, logOut, removeBackendMeetingById,
  refreshUploadedTranscriptsRequest, backendMeetingDataToLocalMeetingData,
  openCheckoutSessionInNewTab, isBrieflySuccess
} from '../utils/genericUtils'
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react'
import { BrieflyNotInstalledState } from './edgeCaseStates/BrieflyNotInstalledState'
import { UserNotLoggedInState } from './edgeCaseStates/UserNotLoggedInState'
import { NoCallsYet } from './edgeCaseStates/NoCallsYet'
import { MobileNotAvailableState } from './edgeCaseStates/MobileNotAvailableState'
import { NotChromeState } from './edgeCaseStates/NotChromeState'
import { BlockedAccessState } from './edgeCaseStates/BlockedAccessState'
import { isMobile } from 'react-device-detect';
import { useBrieflyExtensionVersion } from './hooks/useBrieflyExtensionVersion'
import { useBrieflyExtensionStorageArray } from './hooks/useBrieflyExtensionStorageArray'
import { useDataFromEndpoint } from "./hooks/useDataFromEndpoint";
import ZoomStep1Png from '../images/zoom-step-1.png'
import ZoomStep2Png from '../images/zoom-step-2.png'
import ZoomStep3Png from '../images/zoom-step-3.png'
import ZoomStep4Png from '../images/zoom-step-4.png'
import MsTeamsStep1Png from '../images/ms-teams-step-1.png'
import MsTeamsStep2Png from '../images/ms-teams-step-2.png'
import SortByDropdown from './controls/SortByDropdown'
import { useDebounce } from 'use-debounce';
import { ThreeDots } from 'react-loader-spinner'
import { GoogleOAuthProvider } from '@react-oauth/google';
import useInterval from 'use-interval'
import GoogleCalendarIcon from '../images/google_calendar_icon.png'
import GoogleDriveIcon from '../images/google_drive_icon.png'
import GoogleMeetIcon from '../images/google_meet_icon.png'
import UploadIcon from '../images/upload-icon.png'
import ZoomIcon from '../images/zoom_icon.png'
import SlackIcon from '../images/slack_icon.png'
import HubspotIcon from '../images/hubspot_icon.png'
import HubspotIconSvg from '../images/hubspot.svg'
import SalesforceIcon from '../images/salesforce_icon.png'
import { useUserData } from './hooks/useUserData';
import { Tooltip } from 'react-tooltip'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import FeedbackComponents from './FeedbackComponents'
import ZoomOnboardingPopup from './ZoomOnboardingPopup'
import useScrollOnDrag from 'react-scroll-ondrag';
import useUserEventTracking from "../utils/useUserEventTracking";


const MySwal = withReactContent(Swal)

function RenewUserInfoIntervalComponent() {

  const TICK_TIME = 500
  const TICKS_TIL_RENEW = 50

  const [tick, setTick] = useState(TICKS_TIL_RENEW)

  useInterval(() => {

    setTick(prevTick => {
      if (prevTick === TICKS_TIL_RENEW || localStorage.getItem('user-force-refresh-now')) {
        getRenewedUserInfo();
        localStorage.removeItem('user-force-refresh-now');
        return 0;
      }
      return prevTick + 1;
    });

  }, TICK_TIME)
}

function getMeetingLabel(m) {
  if (m.meeting_status && m.meeting_status !== 'ready') {
    if (m.meeting_status === "ongoing") {
      if (!m.recording_started_zoom) {
        if (!m.meeting_ended_zoom) {
          return m.meetingName
        } else {
          return "Meeting ended. Recording was not started"
        }
      } else {
        return "Transcribing..."
      }
    }
    if (m.meeting_status === "processing") {
      return "Processing..."
    }
    if (m.meeting_status === "captions_missing") {
      return "Transcript not available"
    }
    if (m.meeting_status === "recording_permission_denied") {
      return "Permission denied"
    }

    return "error"
  }
  return m.meetingName
}

function getMeetingIcon(m) {
  if (m.meetingPlatform === 'zoom') {
    return ZoomIcon
  }
  if (m.meetingPlatform == 'Google Meets') {
    return GoogleMeetIcon
  }
  if (m.isBackend) {
    return UploadIcon
  }
  return GoogleMeetIcon
}

export default function Root(params) {

  const LOGGED_IN_AT_LEAST_ONCE_KEY = !!localStorage.getItem('LOGGED_IN_AT_LEAST_ONCE_KEY')

  const { meetingDataById: initialMeetingDataById, brieflyIsInstalled, brieflyUserData } = useLoaderData();
  const navigate = useNavigate();

  const [meetingDataById, setMeetingDataById] = useState(initialMeetingDataById)

  const chronologicalMeetingDataByDay = useMemo(() => {
    return mapObject(groupBy(Object.values(meetingDataById).filter(meeting => meeting.meetingStartDate), meeting => meeting.meetingStartDate.split('T')[0]), lst => lst.sort(function (a, b) {
      return new Date(b.meetingStartDate) - new Date(a.meetingStartDate);
    }))
  }, [meetingDataById])

  const loadingMeetingIds = Object.values(meetingDataById).filter(m => ["ongoing", "processing"].includes(m.meeting_status)).map(x => x.transcriptId)

  useInterval(() => {
    refreshUploadedTranscriptsRequest({
      refresh_transcript_ids: loadingMeetingIds,
      get_transcript_ids_after: Math.max(...Object.values(meetingDataById).filter(x => x.isBackend).filter(x => x.transcriptId).map(x => x.transcriptId).concat([0]))
    }).then(data => {
      const newMeetingDataById = { ...meetingDataById }

      const transcripts = data.transcripts

      for (const transcript of transcripts) {
        const formattedMeeting = backendMeetingDataToLocalMeetingData(transcript)

        newMeetingDataById[formattedMeeting.meetingId] = formattedMeeting

        setMeetingDataById(newMeetingDataById)
      }
    })
  }, 5000)

  const [userData, forceRenewUserData] = useUserData()
  useUserEventTracking({ id: userData?.google_data?.id })

  const [referrerData, setReferrerData] = useState(localStorage.getItem("SUBMITTED_REFERRER_DATA"))
  const [showReferrerNotification, setShowReferrerNotification] = useState(!!referrerData)

  const [showSlackTip, setShowSlackTip] = useState(false)

  useEffect(() => {
    if (showReferrerNotification) {
      localStorage.removeItem("SUBMITTED_REFERRER_DATA")
    }
  })

  const isReferrerSubmitSuccessful = referrerData === userData.referrer_data

  const {
    is_integration_active_google_calendar,
    is_integration_active_zoom,
    is_integration_active_google_drive,
    is_integration_active_hubspot,
    is_integration_active_salesforce,
    subscription_is_active,
    pending_invites,
    admin_user,
    managed_users_not_confirmed,
    managed_users,
    is_onboarding_completed,
    admin_user_subscription_is_active,
    subscription_plan,
    slack_integration_is_visible,
    slack_workspace_name,
    zoom_is_setup
  } = userData

  const isTeam = subscription_is_active && managed_users.concat(managed_users_not_confirmed).length > 0 && subscription_plan !== 'STANDARD_GIVEN_FOR_FREE'
  const isPro = !isTeam && subscription_is_active && subscription_plan !== 'STANDARD_GIVEN_FOR_FREE'
  const isTeamMember = admin_user && subscription_plan !== 'STANDARD_GIVEN_FOR_FREE'
  const isFree = !isTeam && !isPro && !isTeamMember && subscription_plan !== 'STANDARD_GIVEN_FOR_FREE'
  const isFreePro = subscription_plan === 'STANDARD_GIVEN_FOR_FREE'

  const horizontalScrollRef = useRef();
  const { events: horizontalScrollEvents } = useScrollOnDrag(horizontalScrollRef, {
    // runScroll: ({ dx }) => {
    //     alert('hey')
    //     horizontalScrollRef.current.scrollLeft += dx * 5; // eslint-disable-line no-param-reassign
    //   }
  });

  const isHubspotConnected = !!userData?.is_hubspot_connected || !!userData?.team_hubspot_admin
  const isSalesforceConnected = !!userData?.is_salesforce_connected || !!userData?.team_salesforce_admin
  const isGrowthOpportunitiesVisible = !!userData?.hubspot_integration_is_visible || !!userData?.salesforce_integration_is_visible
  const isSlackConnected = !!userData?.slack_workspace_name

  const isHubspotAdmin = isHubspotConnected
  const isSalesforceAdmin = isSalesforceConnected

  const isTeamAccount = managed_users ? (managed_users.concat(managed_users_not_confirmed)).length > 0 : false
  const isTeamMemberAccount = !!admin_user

  const isProGivenForFree = subscription_plan === 'STANDARD_GIVEN_FOR_FREE' && !isTeamMemberAccount

  const urlSearchParams = new URLSearchParams(window.location.search)
  const searchQueryParam = urlSearchParams.get('search')

  const meetingIdsWhereToMigrateSummariesFromExtensionStorageToLocalStorage = Object.values(meetingDataById).filter(m => {
    const key = 'summary-cache-' + m.meetingId
    if (localStorage.getItem(key) === "[object Object]") {
      localStorage.removeItem(key)
    }
    return !m.isBackend && !localStorage.getItem(key)
  }).map(m => m.meetingId)

  const version = useBrieflyExtensionVersion()

  const MAX_STORED_SUMMARIES = 1000

  const getSummaryStorageLeft = () => {
    var count = 0
    for (const key in localStorage) {
      if (key.includes('summary-cache-')) {
        count++
      }
    }
    return Math.max(0, MAX_STORED_SUMMARIES - count)
  }




  const _ = useBrieflyExtensionStorageArray({
    storageKeys: meetingIdsWhereToMigrateSummariesFromExtensionStorageToLocalStorage.map(id => id + '-cache-summary'),
    defaultValue: null,
    processOnInitialLoad: data => {
      var storageLeft = getSummaryStorageLeft()

      for (var meetingId in data) {
        var meetingSummary = data[meetingId]

        const LOCASTORAGE_SUMMARY_KEY = 'summary-cache-' + meetingId

        if (storageLeft--) {
          localStorage.setItem(LOCASTORAGE_SUMMARY_KEY, JSON.stringify({
            ...meetingSummary,
            timestamp: new Date(1) // Highest priority for deletion when overloaded
          }))
        }
      }
    }
  })

  const [isPastCallsPanelCollapsed, setIsPastCallsPanelCollapsed] = useState(false)

  const [showZoomTranscriptInfoPopup, setShowZoomTranscriptInfoPopup] = useState(false)
  const [showZoomOnboardingPopup, setShowZoomOnboardingPopup] = useState(false)
  const [showMsTeamsTranscriptInfoPopup, setShowMsTeamsTranscriptInfoPopup] = useState(false)

  const [showOutOfCreditsPopup, setShowOutOfCreditsPopup] = useState(false)

  const [searchQuery, setSearchQuery] = useState(searchQueryParam || "")
  const [searchQueryDebounce] = useDebounce(searchQuery, 1000);
  const [searchResults, setSearchResults] = useState(null)


  const SORTING_NEWEST_KEY = 'newest'
  const SORTING_OLDEST_KEY = 'oldest'
  const SORTING_BEST_MATCH_KEY = 'best_match'

  const SORTING_CONFIG = [
    {
      key: SORTING_NEWEST_KEY,
      label: 'Newest',
      apply: results => Object.entries(results).sort((a, b) => new Date(b[0]) - new Date(a[0]))
    }, {
      key: SORTING_OLDEST_KEY,
      label: 'Oldest',
      apply: results => Object.entries(results).sort((a, b) => -(new Date(b[0]) - new Date(a[0])))
    }, {
      key: SORTING_BEST_MATCH_KEY,
      label: 'Best Match',
      apply: (results, search) => {
        var res = []
        var i = 0
        for (var date in results) {
          for (var result of results[date]) {
            res.push([
              i++,
              [result]
            ])
          }
        }

        const priority = meeting => {
          var { meetingName, transcriptMatchesCount, summaryMatchesCount, outputMatchesCount } = meeting

          transcriptMatchesCount = transcriptMatchesCount || 0
          summaryMatchesCount = summaryMatchesCount || 0
          outputMatchesCount = outputMatchesCount || 0

          const totalMatchGroups = [transcriptMatchesCount, summaryMatchesCount, outputMatchesCount].filter(x => x > 0).length
          const totalMatches = [transcriptMatchesCount, summaryMatchesCount, outputMatchesCount].reduce((x, y) => x + y, 0)
          const isMeetingNameMatch = getContextStrings(meetingName, search.split('/\s+/'), 1).length > 0
          const isFullMeetingNameMatch = countWordSetMatches(meetingName, search.split('/\s+/')) > 0

          const GROUPS_CNT_WEIGHT = 5
          const TOTAL_MATCHES_WEIGHT = 1
          const MEETING_NAME_WEIGHT = 4
          const MEETING_NAME_FULL_MATCH_WEIGHT = 4


          return totalMatchGroups * GROUPS_CNT_WEIGHT + TOTAL_MATCHES_WEIGHT * totalMatches + isMeetingNameMatch * MEETING_NAME_WEIGHT + isFullMeetingNameMatch * MEETING_NAME_FULL_MATCH_WEIGHT

        }

        return res.sort((a, b) => priority(b[1][0]) - priority(a[1][0]))
      }
    }]

  const [currentSorting, setCurrentSorting] = useState(SORTING_CONFIG[0])

  useEffect(() => {
    if (!searchQuery) {
      setCurrentSorting(SORTING_CONFIG[0])
    }
  }, [searchQuery])

  useEffect(() => {

    async function f() {

      if (!searchQueryDebounce) {
        return;
      }

      const words = searchQueryDebounce.split(' ').map(w => w.toLowerCase())

      const localItemsThatMatch = Object.values(meetingDataById).filter(x => !x.isBackend).filter(meeting => {
        return !words.map(w => {
          if (!meeting.transcriptionBlocks) {
            return false
          }
          return !!meeting.meetingName.toLowerCase().startsWith(w) || !!meeting.transcriptionBlocks.find(b => b.text.toLowerCase().startsWith(w))
        }).includes(false)

      }).map(meeting => {
        return {
          ...meeting,
          transcriptContextStrings: getContextStrings(getTranscriptionAsText(meeting), words, 5),
          transcriptMatchesCount: countWordSetMatches(getTranscriptionAsText(meeting), words)
        }
      })

      const backendItemsThatMatch = (await getUploadedTranscriptsRequest(searchQueryDebounce)).transcripts.map(x => {
        return {
          ...meetingDataById['bkend-' + x.transcriptId],
          ...x
        }
      }).map(meeting => ({
        ...meeting,
        transcriptContextStrings: meeting.search_context_strings,
        transcriptMatchesCount: meeting.search_matches_count
      }))

      const promptsSummariesItemsThatMatch = Object.values(meetingDataById).map(meeting => {
        const prompts = JSON.parse(localStorage.getItem(meeting.meetingId + '-customised-prompts')) || []
        const summary = JSON.parse(localStorage.getItem('summary-cache-' + meeting.meetingId))

        const outputContextStrings = prompts ? getContextStringsFromList(prompts.map(x => x.output), words, 5) : []

        const summaryItemsList = summary ? [
          summary.short_summary,
          summary.long_summary,
          summary.key_insights,
          summary.action_items
        ] : []

        const summaryContextStrings = summary ? getContextStringsFromList(summaryItemsList, words, 5) : []

        const outputMatchesCount = prompts ? countWordSetMatches(prompts.map(x => x.output).join(' '), words) : 0
        const summaryMatchesCount = prompts ? countWordSetMatches(summaryItemsList.join(' '), words) : 0

        return {
          ...meeting,
          outputContextStrings,
          summaryContextStrings,
          outputMatchesCount,
          summaryMatchesCount
        }
      }).filter(x => x.outputMatchesCount > 0 || x.summaryMatchesCount > 0)

      setSearchResults(
        mapObject(
          groupBy(
            mergeObjectsByMeetingId(localItemsThatMatch.concat(backendItemsThatMatch).concat(promptsSummariesItemsThatMatch)),
            meeting => meeting.meetingStartDate.split('T')[0]
          ),
          lst => lst.sort(function (a, b) {
            return new Date(b.meetingStartDate) - new Date(a.meetingStartDate);
          })
        ))
    }

    f()
  }, [searchQueryDebounce])

  const SHOW_UPLOAD_TIP_KEY = 'SHOW_UPLOAD_TIP_KEY'
  const SHOW_DRIVE_TIP_KEY = 'SHOW_DRIVE_TIP_KEY'
  const SHOW_PAYMENT_TIP_KEY = 'SHOW_PAYMENT_TIP_KEY'
  const [workflowRuns] = useDataFromEndpoint(`/api/workflow_runs`)
  const workflowRunsByTranscriptId = useMemo(() => {
    if (workflowRuns == null) return {};

    return workflowRuns.reduce((result, workflowRun) => {
      if (result[workflowRun.transcriptId]) return result;
      result[workflowRun.transcriptId] = workflowRun;
      return result;
    }, {});
  }, [workflowRuns?.length])

  const showUploadTipIcon = false
  const [showUploadTip, setShowUploadTip] = useState(!localStorage.getItem(SHOW_UPLOAD_TIP_KEY))
  const [showDriveTip, setShowDriveTip] = useState(!localStorage.getItem(SHOW_DRIVE_TIP_KEY))
  const [_showPaymentTip, setShowPaymentTip] = useState(!localStorage.getItem(SHOW_PAYMENT_TIP_KEY))
  const showPaymentTip = false;//_showPaymentTip && !userData.subscription_is_active && !userData.error && new Date(userData?.date_registered) < new Date("2023-08-08T13:06:28.144756Z")
  const [paymentTipSelectedPlan, setPaymentTipSelectedPlan] = useState('free')

  const [removedMeetingIds, setRemovedMeetingIds] = useState([])

  const [showRemoveMeetingPopupForMeeting, setShowRemoveMeetingPopupForMeeting] = useState(null)

  function removeMeeting(meetingId) {
    removeMeetingById(meetingId)

    setRemovedMeetingIds(removedMeetingIds.concat([meetingId]))
  }

  function removeBackendMeeting(meetingId) {
    removeBackendMeetingById(meetingId)

    setRemovedMeetingIds(removedMeetingIds.concat([meetingId]))
  }

  function removeBackendMeeting(meetingId) {
    removeBackendMeetingById(meetingId)

    setRemovedMeetingIds(removedMeetingIds.concat([meetingId]))
  }

  useEffect(() => {
    if (isBrieflySuccess()) {
      return;
    }
    if (window.location.pathname === '/' && chronologicalMeetingDataByDay && Object.keys(chronologicalMeetingDataByDay).length > 0) {
      const latestMeeting = Object.entries(chronologicalMeetingDataByDay).sort((a, b) => new Date(b[0]) - new Date(a[0]))[0][1][0]
      navigate(`/meeting/${latestMeeting.meetingId}${searchQueryParam ? `?search=${searchQueryParam}` : ''}`)
    }
  }, [])

  var body = null
  var showIntegrationsState = false

  var INTEGRATIONS_CONFIG = [
    {
      icon: GoogleMeetIcon,
      is_enabled: brieflyIsInstalled,
      label: 'Google Meet',
      onClick: !brieflyIsInstalled ? () => window.open('https://chromewebstore.google.com/detail/briefly-ai-meeting-summar/bjmgcelbpkgmofiogkmleblcmecflldk', '_blank').focus() : null
    },
    {
      icon: ZoomIcon,
      is_enabled: zoom_is_setup,
      label: 'Zoom',
      onClick: zoom_is_setup ? () => navigate('/settings/integrations/zoom') : () => setShowZoomOnboardingPopup(true)
    },
    {
      icon: GoogleCalendarIcon,
      is_enabled: is_integration_active_google_calendar,
      label: 'Google Calendar',
      onClick: () => navigate('/settings/integrations')
    },
    // {
    //   icon: GoogleDriveIcon,
    //   is_enabled: is_integration_active_google_drive,
    //   label: 'Google Drive'
    // },
    {
      icon: SlackIcon,
      is_enabled: !!slack_workspace_name,
      label: 'Slack',
      onClick: () => navigate('/settings/integrations/slack')
    },
    {
      icon: HubspotIconSvg,
      is_enabled: is_integration_active_hubspot,
      label: 'Hubspot',
      onClick: () => navigate('/settings/integrations/hubspot')
    },
    {
      icon: SalesforceIcon,
      is_enabled: is_integration_active_salesforce,
      label: 'Salesforce',
      onClick: () => navigate('/settings/integrations/salesforce')
    }
  ]

  if (isMobile) {
    body = <div id="content">
      <MobileNotAvailableState />
    </div>
  } else if (!window.chrome) {
    body = <div id="content">
      <NotChromeState />
    </div>
  } else if (userData.organization && userData.organization.block_free_access && !(isTeamAccount || isTeamMemberAccount)) {
    body = <div id="content">
      <BlockedAccessState organizationData={userData.organization} />
    </div>
  } else if (brieflyIsInstalled && userData.error) {
    body = <div id="content" className={LOGGED_IN_AT_LEAST_ONCE_KEY ? 'LOGGED_IN_AT_LEAST_ONCE_KEY' : ''}>
      <GoogleOAuthProvider clientId="576404817524-7ghb7h6lrj86l827mhddqfal0c61bj9n.apps.googleusercontent.com">
        <UserNotLoggedInState />
      </GoogleOAuthProvider>
    </div>
  } else if (Object.keys(chronologicalMeetingDataByDay).length === 0) {
    body = <div id="content">
      <div id="calls-list" className="no-calls">
        No calls available yet...
      </div>
      <div id="detail">
        <NoCallsYet />
      </div>
    </div>
  } else {
    showIntegrationsState = true
    var innerBody = null

    if (isBrieflySuccess()) {
      innerBody = <>This is briefly success</>
    } else {
      innerBody = <>
        <div id="calls-list" className={isPastCallsPanelCollapsed ? 'collapsed' : ''}>
          <div id="hide-past-calls-container">
            <h4>Connect your apps</h4>
            <button id="hide-past-calls" style={{ width: !isPastCallsPanelCollapsed ? 50 : '', marginTop: 3 }}
              onClick={() => setIsPastCallsPanelCollapsed(!isPastCallsPanelCollapsed)}>
              <svg width="11" height="9" view-box="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.90875 8.11596C8.71265 8.32626 8.3816 8.33308 8.177 8.13104L4.5 4.5L8.177 0.868959C8.3816 0.66692 8.71265 0.673742 8.90875 0.884039C9.09829 1.0873 9.0903 1.40487 8.89079 1.59835L5.89873 4.5L8.89079 7.40165C9.0903 7.59513 9.09829 7.9127 8.90875 8.11596Z"
                  fill="#C6C6C6" />
                <path
                  d="M5.90875 8.11596C5.71265 8.32626 5.3816 8.33308 5.177 8.13104L1.5 4.5L5.177 0.86896C5.3816 0.66692 5.71265 0.673742 5.90875 0.884039V0.884039C6.09829 1.0873 6.0903 1.40487 5.89079 1.59835L2.89873 4.5L5.89079 7.40165C6.0903 7.59513 6.09829 7.9127 5.90875 8.11596V8.11596Z"
                  fill="#C6C6C6" />
              </svg>
              {isPastCallsPanelCollapsed ? '' : 'Hide'}
            </button>
          </div>

          {!isPastCallsPanelCollapsed ? <>
            <p style={{
              marginTop: 0,
              "color": "var(--text-text-secondary, #C6C6C6)",
              "fontFeatureSettings": "'clig' off, 'liga' off",
              "fontFamily": "Roboto",
              "fontSize": "14px",
              "fontStyle": "normal",
              "fontWeight": "400",
              "lineHeight": "20px",
              "letterSpacing": "0.15px"
            }}>
              Select which app you want to integrate with Briefly to get started. <a
                onClick={() => navigate('/settings/integrations')} style={{
                  cursor: 'pointer',
                  fontWeight: 700,
                  "color": "var(--color-brand-briefly, #E44867)",
                  "textDecorationLine": "underline"
                }}>
                Manage your integrations in settings.
              </a>
            </p>

            <div className="connect-your-apps-container" {...horizontalScrollEvents} ref={horizontalScrollRef}
              style={{ overflowX: 'scroll', overflowY: 'hidden', paddingTop: 5 }}>
              <div style={{ width: 342, display: 'flex' }}>
                {INTEGRATIONS_CONFIG.map((integration, i) => {
                  const { onClick, icon, label, is_enabled } = integration

                  return <>
                    <div className="integration-item" style={{
                      whiteSpace: 'nowrap',
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginRight: 10,
                      marginBottom: 15,
                      position: 'relative',
                      width: 46,
                      height: 46,
                      cursor: 'pointer',
                      borderRadius: 5,
                      background: '#212121'
                    }} onClick={() => {
                      navigate('/settings/integrations')
                      if (onClick) {
                        onClick()
                      }
                    }} data-tooltip-id={"tooltip-button-" + i}
                      data-tooltip-content={label + (is_enabled ? '' : ' not') + (icon === GoogleMeetIcon ? ' installed' : icon === HubspotIconSvg || icon === SalesforceIcon ? ' enabled' : ' connected')}>
                      <img src={icon} width="27" style={{ pointerEvents: 'none', userSelect: 'none' }} />
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: 8,
                        border: '1px solid white',
                        background: is_enabled ? '#80BF07' : '#FF3A3A',
                        position: 'absolute',
                        top: -3,
                        right: -4
                      }} />
                    </div>
                    <Tooltip id={"tooltip-button-" + i} place="bottom"
                      content={label + (is_enabled ? '' : ' not') + (icon === GoogleMeetIcon ? ' installed' : icon === HubspotIconSvg || icon === SalesforceIcon ? ' enabled' : ' connected')} />
                  </>
                })}
              </div>
            </div>

            <div style={{
              borderBottom: '1px solid #2C2C2C',
              height: 0,
              width: 400,
              position: 'relative',
              left: -50
            }}></div>

          </> : ''}

          <div id="hide-past-calls-container">
            <h4>Past calls</h4>
            <button style={{ opacity: 0, pointerEvents: 'none' }} id="hide-past-calls"
              onClick={() => setIsPastCallsPanelCollapsed(!isPastCallsPanelCollapsed)}>
              <svg width="11" height="9" viewBox="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.90875 8.11596C8.71265 8.32626 8.3816 8.33308 8.177 8.13104L4.5 4.5L8.177 0.868959C8.3816 0.66692 8.71265 0.673742 8.90875 0.884039C9.09829 1.0873 9.0903 1.40487 8.89079 1.59835L5.89873 4.5L8.89079 7.40165C9.0903 7.59513 9.09829 7.9127 8.90875 8.11596Z"
                  fill="#C6C6C6" />
                <path
                  d="M5.90875 8.11596C5.71265 8.32626 5.3816 8.33308 5.177 8.13104L1.5 4.5L5.177 0.86896C5.3816 0.66692 5.71265 0.673742 5.90875 0.884039V0.884039C6.09829 1.0873 6.0903 1.40487 5.89079 1.59835L2.89873 4.5L5.89079 7.40165C6.0903 7.59513 6.09829 7.9127 5.90875 8.11596V8.11596Z"
                  fill="#C6C6C6" />
              </svg>
              {isPastCallsPanelCollapsed ? '' : 'Hide calls list'}
            </button>
          </div>
          {!isPastCallsPanelCollapsed && <button id="upload-transcript-btn" style={{
            position: 'relative', background: showUploadTip ? 'transparent' : ''
          }} onClick={() => {

            trackEvent("briefly-upload-transcript-navigate", {})

            setShowUploadTip(false)
            // setShowUploadTipIcon(false)

            localStorage.setItem(SHOW_UPLOAD_TIP_KEY, '1')

            navigate('/upload-transcript')
          }}>
            Upload a transcript
            {showUploadTipIcon && <div className="ping" onClick={e => {
              e.stopPropagation()
              setShowUploadTip(true);
              // setShowUploadTipIcon(false)
              localStorage.setItem(SHOW_UPLOAD_TIP_KEY, '1')
            }}></div>}
            {showUploadTip && <div className="upload-tip-container">
              <div className="upload-tip-background" onClick={e => {
                e.stopPropagation()
                setShowUploadTip(false)
                localStorage.setItem(SHOW_UPLOAD_TIP_KEY, '1')
              }}></div>
              <div className="upload-tip-content">
                <button id="upload-transcript-btn-duplicate" style={{ width: '100%', height: '100%', marginTop: 0 }}
                  onClick={() => {
                    document.getElementById("upload-transcript-btn").click()
                  }}>
                  Upload a transcript
                </button>
                <div className="tip" onClick={e => e.stopPropagation()} style={{ cursor: 'default' }}>
                  <div className="badge">NEW</div>
                  <p>You can now upload any transcript or text into Briefly</p>
                </div>
              </div>
            </div>}
          </button>}
          <div style={{ position: 'relative' }}>
            <input style={!searchQuery ? {} : { borderColor: '#C6C6C6' }} id="call-search" type="text"
              placeholder="Search calls, transcripts and summaries" value={searchQuery} onChange={e => {
                setSearchResults(null)
                setSearchQuery(e.target.value)
              }} />
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4.74338 9.22315C5.76731 9.22315 6.72339 8.89932 7.50059 8.3596L10.8095 11.8021C10.9452 11.934 11.1241 12 11.3092 12C11.7101 12 12 11.7002 12 11.3164C12 11.1365 11.9383 10.9685 11.8026 10.8366L8.51218 7.4061C9.12284 6.62651 9.48676 5.66102 9.48676 4.61157C9.48676 2.07491 7.35255 0 4.74338 0C2.12804 0 0 2.07491 0 4.61157C0 7.14824 2.12804 9.22315 4.74338 9.22315ZM4.74338 7.65767C2.9037 7.65767 1.60467 6.27 1.60467 4.61157C1.60467 2.85 2.9037 1.56548 4.74338 1.56548C6.38814 1.56548 7.88209 2.85 7.88209 4.61157C7.88209 6.27 6.38814 7.65767 4.74338 7.65767Z"
                fill="#C6C6C6" />
            </svg>
            <svg style={!searchQuery ? { display: 'none' } : {}} onClick={() => {
              setSearchResults(null)
              setSearchQuery('')
            }} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="14" height="14" rx="7" fill="white" />
              <path
                d="M9.69799 4.30201C9.86478 4.46881 9.86478 4.73924 9.69799 4.90604L7.60403 7L9.69799 9.09396C9.86478 9.26076 9.86478 9.53119 9.69799 9.69799C9.53119 9.86478 9.26076 9.86478 9.09396 9.69799L7 7.60403L4.90604 9.69799C4.73924 9.86478 4.46881 9.86478 4.30201 9.69799C4.13522 9.53119 4.13522 9.26076 4.30201 9.09396L6.39597 7L4.30201 4.90604C4.13522 4.73924 4.13522 4.46881 4.30201 4.30201C4.46881 4.13522 4.73924 4.13522 4.90604 4.30201L7 6.39597L9.09396 4.30201C9.26076 4.13522 9.53119 4.13522 9.69799 4.30201Z"
                fill="#141414" />
            </svg>


          </div>

          {searchQueryDebounce && <div style={{ marginTop: 10 }}>
            <SortByDropdown items={SORTING_CONFIG} selectedItem={currentSorting}
              onChange={item => setCurrentSorting(item)} />
          </div>}
          {!searchQuery && Object.entries(chronologicalMeetingDataByDay).sort((a, b) => new Date(b[0]) - new Date(a[0])).map(entry => {
            const date = entry[0]
            const meetings = entry[1]

            if (!meetings.find(m => !removedMeetingIds.includes(m.meetingId))) {
              return ''
            }
            return <div key={date}>
              <span className="meeting-start-date">{formatDate(date)}</span>
              {meetings.filter(m => m.meetingId == 'demo-meeting' || (!removedMeetingIds.includes(m.meetingId) && m.isBackend)).map(m => {
                const hasTasks = workflowRunsByTranscriptId[m.transcriptId]?.tasks.some(({ complete }) => complete === false);

                return <div key={m.meetingId} meetingid={m.meetingId} className="call-label" onClick={() => navigate(`/meeting/${m.meetingId}`)}>
                  <h4 style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {hasTasks && <span style={{ lineHeight: 0, color: '#FF3A3A', fontSize: 24 }}>•</span>} <img src={getMeetingIcon(m)} width="12" /> {getMeetingLabel(m)}
                  </h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span className="meeting-start-date">{formatDatetime(m.meetingStartDate)}</span>
                    <span className="meeting-start-date recording-in-progress">
                      {m.meeting_status === "ongoing" ? <>
                        <svg style={{ marginRight: 3 }} xmlns="http://www.w3.org/2000/svg" width="8" height="8"
                          viewBox="0 0 8 8" fill="none">
                          <circle opacity="0.6" cx="4" cy="4" r="2" fill="#FF3A3A" />
                        </svg>
                        Recording in progress...
                      </> : ''}
                    </span>
                  </div>
                  <span className="delete-icon" onClick={e => {
                    e.stopPropagation();
                    setShowRemoveMeetingPopupForMeeting(m)
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1.5 10.875C1.5 11.5078 1.99219 12 2.625 12H9.375C9.98438 12 10.5 11.5078 10.5 10.875V3H1.5V10.875ZM7.875 4.875C7.875 4.6875 8.03906 4.5 8.25 4.5C8.4375 4.5 8.625 4.6875 8.625 4.875V10.125C8.625 10.3359 8.4375 10.5 8.25 10.5C8.03906 10.5 7.875 10.3359 7.875 10.125V4.875ZM5.625 4.875C5.625 4.6875 5.78906 4.5 6 4.5C6.1875 4.5 6.375 4.6875 6.375 4.875V10.125C6.375 10.3359 6.1875 10.5 6 10.5C5.78906 10.5 5.625 10.3359 5.625 10.125V4.875ZM3.375 4.875C3.375 4.6875 3.53906 4.5 3.75 4.5C3.9375 4.5 4.125 4.6875 4.125 4.875V10.125C4.125 10.3359 3.9375 10.5 3.75 10.5C3.53906 10.5 3.375 10.3359 3.375 10.125V4.875ZM10.875 0.75H8.0625L7.82813 0.328125C7.73438 0.140625 7.52344 0 7.33594 0H4.64063C4.45313 0 4.21875 0.140625 4.14844 0.328125L3.9375 0.75H1.125C0.914062 0.75 0.75 0.9375 0.75 1.125V1.875C0.75 2.08594 0.914062 2.25 1.125 2.25H10.875C11.0625 2.25 11.25 2.08594 11.25 1.875V1.125C11.25 0.9375 11.0625 0.75 10.875 0.75Z"
                        fill="white" />
                    </svg>
                  </span>

                </div>
              })}
            </div>
          })}
          {searchResults && currentSorting.apply(searchResults, searchQueryDebounce).map(entry => {
            const date = entry[0]
            const meetings = entry[1]

            if (!meetings.find(m => !removedMeetingIds.includes(m.meetingId))) {
              return ''
            }

            return <div className="search-result" key={date}>
              {currentSorting.key !== SORTING_BEST_MATCH_KEY &&
                <span className="meeting-start-date">{formatDate(date)}</span>}
              {meetings.filter(m => !removedMeetingIds.includes(m.meetingId)).map(m => {


                const hasTranscriptMatch = m.transcriptMatchesCount > 0
                const hasCreateMatch = m.outputMatchesCount > 0
                const hasSummaryMatch = m.summaryMatchesCount > 0

                return <div key={m.meetingId} meetingid={m.meetingId} className="call-label" onClick={() => navigate(`/meeting/${m.meetingId}${hasCreateMatch ? '?tab=create' : hasTranscriptMatch ? '?tab=transcript' : hasSummaryMatch ? '?tab=summary' : ''}`)}>
                  <h4><img src={getMeetingIcon(m)} width="12" style={{ marginRight: 5 }} />{highlightText(m.meetingName, searchQuery.split(/\s+/), { "fontWeight": "bold" })}</h4>
                  <span className="meeting-start-date">{formatDatetime(m.meetingStartDate)}</span>
                  <span style={m.isBackend ? { display: 'none' } : {}} className="delete-icon" onClick={e => { e.stopPropagation(); setShowRemoveMeetingPopupForMeeting(m) }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1.5 10.875C1.5 11.5078 1.99219 12 2.625 12H9.375C9.98438 12 10.5 11.5078 10.5 10.875V3H1.5V10.875ZM7.875 4.875C7.875 4.6875 8.03906 4.5 8.25 4.5C8.4375 4.5 8.625 4.6875 8.625 4.875V10.125C8.625 10.3359 8.4375 10.5 8.25 10.5C8.03906 10.5 7.875 10.3359 7.875 10.125V4.875ZM5.625 4.875C5.625 4.6875 5.78906 4.5 6 4.5C6.1875 4.5 6.375 4.6875 6.375 4.875V10.125C6.375 10.3359 6.1875 10.5 6 10.5C5.78906 10.5 5.625 10.3359 5.625 10.125V4.875ZM3.375 4.875C3.375 4.6875 3.53906 4.5 3.75 4.5C3.9375 4.5 4.125 4.6875 4.125 4.875V10.125C4.125 10.3359 3.9375 10.5 3.75 10.5C3.53906 10.5 3.375 10.3359 3.375 10.125V4.875ZM10.875 0.75H8.0625L7.82813 0.328125C7.73438 0.140625 7.52344 0 7.33594 0H4.64063C4.45313 0 4.21875 0.140625 4.14844 0.328125L3.9375 0.75H1.125C0.914062 0.75 0.75 0.9375 0.75 1.125V1.875C0.75 2.08594 0.914062 2.25 1.125 2.25H10.875C11.0625 2.25 11.25 2.08594 11.25 1.875V1.125C11.25 0.9375 11.0625 0.75 10.875 0.75Z"
                        fill="white" />
                    </svg>
                  </span>
                  {hasTranscriptMatch && <div className="search-match-info">
                    <div className="search-badge-container" onClick={e => {
                      e.stopPropagation();
                      navigate(`/meeting/${m.meetingId}?tab=transcript`)
                    }}>
                      <div className="search-badge-name">Transcript</div>
                      <div
                        className="search-badge-count">{m.transcriptMatchesCount} {plural('match', 'matches', m.transcriptContextStrings.length)}</div>
                    </div>
                    <p>
                      {highlightText(m.transcriptContextStrings?.slice(0, 3)?.join(" ... ") + (m.transcriptContextStrings.length > 1 ? " ... " : ""), searchQuery.split(/\s+/), { "fontWeight": "bold" })}
                    </p>
                  </div>}
                  {hasSummaryMatch && <div className="search-match-info">
                    <div className="search-badge-container" onClick={e => {
                      e.stopPropagation();
                      navigate(`/meeting/${m.meetingId}?tab=summary`)
                    }}>
                      <div className="search-badge-name">AI Summary</div>
                      <div
                        className="search-badge-count">{m.summaryMatchesCount} {plural('match', 'matches', m.summaryContextStrings.length)}</div>
                    </div>
                    <p>
                      {highlightText(m.summaryContextStrings?.slice(0, 3)?.join(" ... ") + (m.summaryContextStrings.length > 1 ? " ... " : ""), searchQuery.split(/\s+/), { "fontWeight": "bold" })}
                    </p>
                  </div>}
                  {hasCreateMatch && <div className="search-match-info">
                    <div className="search-badge-container" onClick={e => {
                      e.stopPropagation();
                      navigate(`/meeting/${m.meetingId}?tab=create`)
                    }}>
                      <div className="search-badge-name">Create</div>
                      <div
                        className="search-badge-count">{m.outputMatchesCount} {plural('match', 'matches', m.outputMatchesCount)}</div>
                    </div>
                    <p>
                      {highlightText(m.outputContextStrings?.slice(0, 3)?.join(" ... ") + (m.outputContextStrings.length > 1 ? " ... " : ""), searchQuery.split(/\s+/), { "fontWeight": "bold" })}
                    </p>
                  </div>}
                </div>
              })}
            </div>
          })}
          {searchQuery && !searchResults && <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <ThreeDots
              height="35"
              width="35"
              radius="9"
              color="#FFFFFF"
              ariaLabel="three-dots-loading"
              wrapperStyle={{}}
              wrapperClassName=""
              visible={true}
            />
          </div>}
        </div>
        <div id="detail">
          <Outlet context={{
            setShowSlackTip,
            brieflyUserData,
            brieflyIsInstalled,
            meetingDataById,
            setShowZoomTranscriptInfoPopup,
            setShowZoomOnboardingPopup,
            setShowMsTeamsTranscriptInfoPopup,
            setShowOutOfCreditsPopup,
            searchWords: searchQueryDebounce ? searchQueryDebounce.split(/\s+/) : null
          }} />
        </div>
      </>
    }

    body = <div id="content">
      {innerBody}
    </div>
  }
  if (!userData) return <></>
  return (
    <div id="body" {...{
      "subscription-type": isTeam ? 'is-team' : isTeamMember ? 'is-team-member' : isPro || isFreePro ? 'is-pro' : 'is-free',
      "hubspot-connected": isHubspotConnected ? 'true' : 'false',
      "salesforce-connected": isSalesforceConnected ? 'true' : 'false',
      "is-hubspot-admin": isHubspotAdmin ? 'true' : 'false',
      "is-salesforce-admin": isSalesforceAdmin ? 'true' : 'false',
      "slack-connected": isSlackConnected ? 'true' : 'false',
      "growth-opportunities-visible": isGrowthOpportunitiesVisible ? 'true' : 'false'
    }}
    >
      {/*{JSON.stringify({ brieflyIsInstalled, userData })}*/}
      {/*{JSON.stringify({version})}*/}

      <RenewUserInfoIntervalComponent />

      {!userData?.error ? <FeedbackComponents /> : ''}

      <div id="top-nav" style={{
        background: 'linear-gradient(90deg, #B700AF 0%, #F16617 100%)',
        display: brieflyIsInstalled && userData.error ? 'none' : 'flex',
        justifyContent: 'space-between'
      }}>
        <div>
          <svg style={{
            margin: '6px 15px',
            marginTop: '10px'
          }} width="90" height="25" viewBox="0 0 119 35" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M39.0792 29.7742V12.3396H45.3556C46.5716 12.3396 47.6803 12.4648 48.6816 12.7151C49.683 12.9655 50.4787 13.4036 51.0688 14.0294C51.6589 14.6553 51.954 15.5404 51.954 16.6849C51.954 17.4359 51.7573 18.1869 51.3639 18.938C50.9705 19.6711 50.434 20.1718 49.7545 20.44V20.5473C50.595 20.7798 51.3013 21.2268 51.8735 21.8884C52.4636 22.55 52.7586 23.462 52.7586 24.6243C52.7586 25.8224 52.4457 26.8059 51.8198 27.5748C51.194 28.3258 50.3625 28.8801 49.3254 29.2378C48.2882 29.5954 47.1438 29.7742 45.8921 29.7742H39.0792ZM43.6927 19.1525H45.2484C45.9994 19.1525 46.5537 18.9916 46.9113 18.6697C47.269 18.3479 47.4478 17.9187 47.4478 17.3822C47.4478 16.8458 47.269 16.4613 46.9113 16.2289C46.5537 15.9964 45.9994 15.8802 45.2484 15.8802H43.6927V19.1525ZM43.6927 26.2337H45.5702C47.3584 26.2337 48.2525 25.5899 48.2525 24.3024C48.2525 23.6587 48.0289 23.2027 47.5819 22.9345C47.1527 22.6663 46.4822 22.5322 45.5702 22.5322H43.6927V26.2337Z"
              fill="white" />
            <path
              d="M55.1272 29.7742V16.363H58.8823L59.2042 18.6697H59.3115C59.8122 17.7578 60.4023 17.0961 61.0818 16.6849C61.7791 16.2557 62.4586 16.0411 63.1203 16.0411C63.5315 16.0411 63.8624 16.068 64.1127 16.1216C64.3809 16.1574 64.6044 16.2199 64.7833 16.3094L64.0322 20.2791C63.7998 20.2254 63.5673 20.1807 63.3348 20.145C63.1024 20.0913 62.8342 20.0645 62.5302 20.0645C62.0474 20.0645 61.5377 20.2344 61.0013 20.5741C60.4827 20.896 60.0625 21.4771 59.7406 22.3176V29.7742H55.1272Z"
              fill="white" />
            <path
              d="M66.3381 29.7742V16.363H70.9516V29.7742H66.3381ZM68.6448 14.7C67.8938 14.7 67.2858 14.4944 66.8209 14.0831C66.356 13.6539 66.1235 13.0907 66.1235 12.3933C66.1235 11.6959 66.356 11.1416 66.8209 10.7303C67.2858 10.3011 67.8938 10.0865 68.6448 10.0865C69.3959 10.0865 70.0038 10.3011 70.4688 10.7303C70.9337 11.1416 71.1662 11.6959 71.1662 12.3933C71.1662 13.0907 70.9337 13.6539 70.4688 14.0831C70.0038 14.4944 69.3959 14.7 68.6448 14.7Z"
              fill="white" />
            <path
              d="M80.2881 30.0961C78.9827 30.0961 77.8025 29.8189 76.7475 29.2646C75.7104 28.7103 74.8878 27.9145 74.2799 26.8774C73.6719 25.8224 73.3679 24.5528 73.3679 23.0686C73.3679 21.6202 73.6719 20.3774 74.2799 19.3403C74.9057 18.2853 75.7104 17.4717 76.6939 16.8994C77.6774 16.3272 78.7145 16.0411 79.8053 16.0411C81.1285 16.0411 82.2193 16.3362 83.0776 16.9263C83.9538 17.4985 84.5976 18.2763 85.0088 19.2598C85.438 20.2433 85.6526 21.3341 85.6526 22.5322C85.6526 22.9256 85.6257 23.31 85.5721 23.6855C85.5363 24.0432 85.5006 24.3024 85.4648 24.4634H77.8204C78.035 25.268 78.4195 25.8313 78.9738 26.1532C79.5281 26.4572 80.1808 26.6092 80.9318 26.6092C81.7901 26.6092 82.7021 26.3409 83.6677 25.8045L85.1698 28.5404C84.4545 29.0411 83.6498 29.4255 82.7557 29.6937C81.8617 29.962 81.0391 30.0961 80.2881 30.0961ZM77.7668 21.4593H81.7365C81.7365 20.9407 81.6024 20.4937 81.3342 20.1181C81.0838 19.7247 80.61 19.528 79.9126 19.528C79.4119 19.528 78.9648 19.68 78.5714 19.984C78.1781 20.288 77.9098 20.7798 77.7668 21.4593Z"
              fill="white" />
            <path
              d="M88.8312 29.7742V19.9572H87.061V16.5239L88.8312 16.3898V16.2021C88.8312 15.2365 88.9922 14.3424 89.314 13.5198C89.6538 12.6973 90.2081 12.0356 90.977 11.535C91.7638 11.0343 92.8188 10.7839 94.1421 10.7839C94.7679 10.7839 95.3223 10.8376 95.8051 10.9449C96.3058 11.0522 96.6992 11.1594 96.9853 11.2667L96.1806 14.6464C95.6978 14.4675 95.2507 14.3781 94.8395 14.3781C94.4103 14.3781 94.0706 14.5033 93.8202 14.7537C93.5699 14.9861 93.4447 15.4153 93.4447 16.0411V16.363H95.6978V19.9572H93.4447V29.7742H88.8312Z"
              fill="white" />
            <path
              d="M101.823 30.0961C100.786 30.0961 99.963 29.8904 99.3551 29.4792C98.765 29.05 98.3358 28.4599 98.0676 27.7089C97.8172 26.94 97.6921 26.0548 97.6921 25.0535V11.1058H102.306V25.2144C102.306 25.6614 102.386 25.9744 102.547 26.1532C102.726 26.3141 102.896 26.3946 103.057 26.3946C103.146 26.3946 103.217 26.3946 103.271 26.3946C103.343 26.3767 103.432 26.3588 103.539 26.3409L104.076 29.7206C103.843 29.8279 103.539 29.9173 103.164 29.9888C102.788 30.0603 102.341 30.0961 101.823 30.0961Z"
              fill="white" />
            <path
              d="M107.666 34.8168C107.219 34.8168 106.843 34.79 106.539 34.7364C106.236 34.6827 105.932 34.6201 105.628 34.5486L106.432 31.0617C106.557 31.0796 106.7 31.1064 106.861 31.1422C107.022 31.1958 107.165 31.2226 107.291 31.2226C107.916 31.2226 108.39 31.0975 108.712 30.8471C109.034 30.5968 109.257 30.2749 109.383 29.8815L109.544 29.2914L104.447 16.363H109.061L110.67 21.5129C110.867 22.1209 111.037 22.7378 111.18 23.3637C111.323 23.9895 111.475 24.6422 111.636 25.3217H111.743C111.886 24.678 112.02 24.0432 112.145 23.4173C112.288 22.7914 112.44 22.1566 112.601 21.5129L113.942 16.363H118.341L113.835 29.5596C113.37 30.7577 112.87 31.7412 112.333 32.5101C111.815 33.2969 111.18 33.878 110.429 34.2536C109.696 34.6291 108.775 34.8168 107.666 34.8168Z"
              fill="white" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M32.4903 8.01119C32.7999 7.82501 33.2019 7.9251 33.3881 8.23474L33.7106 8.77115C36.317 13.1058 33.7562 18.7059 28.7726 19.5699L28.3833 19.6374L28.7112 19.9286C33.1047 23.8298 30.5274 31.0955 24.6577 31.3561L22.6513 31.4452L0.817433 32.2533C0.456373 32.2667 0.152842 31.9848 0.139479 31.6238C0.126115 31.2627 0.40798 30.9592 0.76904 30.9458L22.5956 30.138L22.6005 30.1378L24.5997 30.049C29.2955 29.8405 31.3573 24.0279 27.8425 20.907L25.4833 18.8122L28.5491 18.2807C32.6266 17.5738 34.7218 12.9919 32.5893 9.44538L32.2667 8.90897C32.0806 8.59932 32.1806 8.19738 32.4903 8.01119Z"
              fill="white" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M6.36892 23.3956C6.4285 18.3638 8.24859 13.4641 11.0437 9.68252L11.8387 18.1786L11.7845 18.1836L11.8394 18.1869L11.8446 18.2417L11.8477 18.1873L20.6763 18.7035C16.8889 21.7674 12.1117 23.6642 6.88394 23.8358C5.70664 26.3012 3.79692 28.3574 1.40102 29.7131L1.23397 29.8077C2.22815 27.0673 3.93696 24.9761 6.36892 23.3956ZM11.8483 18.1777L12.4454 7.96369C13.3295 6.97935 14.2968 6.07084 15.3366 5.24939L16.3155 13.5396L16.2629 13.5458L16.3167 13.5503L16.3231 13.6039L16.3274 13.5512L24.8896 14.2602C24.1312 15.3069 23.2871 16.2881 22.3676 17.1933L11.8483 18.1777ZM16.3285 13.5381L17.1207 3.97203C17.9323 3.44711 18.779 2.97148 19.6567 2.54944L20.7551 8.93617L20.7064 8.94454L20.7577 8.95154L20.7665 9.00263L20.7732 8.95364L27.3968 9.856C27.0207 10.7269 26.5926 11.5703 26.1164 12.3824L16.3285 13.5381ZM20.776 8.93256L21.7666 1.6612C24.1152 0.807394 26.643 0.321986 29.2816 0.27593C29.3265 2.84733 28.9507 5.32819 28.2176 7.65283L20.776 8.93256Z"
              fill="white" />
          </svg>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>

          {!isProGivenForFree && !userData.error && !subscription_is_active && !isTeamMemberAccount || (isTeamMemberAccount && !admin_user_subscription_is_active) ?
            <div id="free-ai-credits-left">
              {userData.briefly_ai_credits_left} free AI credits left
              <div className="ai-credits-help-container">
                <svg style={{ cursor: 'pointer', position: 'relative', top: 2, marginLeft: 3 }} openOnClick={true}
                  data-tooltip-id={"tooltip-button-ai-credits"} width="12" height="13" viewBox="0 0 12 13"
                  fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.83099 6.0493C8.20657 5.67371 8.39437 5.223 8.39437 4.69718C8.39437 4.03991 8.15962 3.47653 7.69014 3.00704C7.22066 2.53756 6.65728 2.30282 6 2.30282C5.34272 2.30282 4.77934 2.53756 4.30986 3.00704C3.98644 3.33046 3.77442 3.69844 3.6738 4.11098C3.59638 4.42838 3.87048 4.69718 4.19718 4.69718C4.52389 4.69718 4.77519 4.41515 4.93788 4.13184C4.99426 4.03368 5.0666 3.94044 5.15493 3.85211C5.39906 3.60798 5.68075 3.48592 6 3.48592C6.31925 3.48592 6.60094 3.60798 6.84507 3.85211C7.0892 4.09624 7.21127 4.37793 7.21127 4.69718C7.21127 5.01643 7.0892 5.29812 6.84507 5.54225L6.11268 6.30282C5.64319 6.80986 5.40845 7.37324 5.40845 7.99296C5.40845 8.16409 5.54718 8.30282 5.71831 8.30282H6C6.3267 8.30282 6.5831 8.03365 6.66565 7.71755C6.76853 7.32354 6.97858 6.95525 7.29578 6.61268L7.83099 6.0493ZM6 10.6972C6.3267 10.6972 6.59155 10.4323 6.59155 10.1056C6.59155 9.77893 6.3267 9.51408 6 9.51408C5.6733 9.51408 5.40845 9.77893 5.40845 10.1056C5.40845 10.4323 5.6733 10.6972 6 10.6972ZM1.74648 2.27465C2.92958 1.09155 4.34742 0.5 6 0.5C7.65258 0.5 9.06103 1.09155 10.2254 2.27465C11.4085 3.43897 12 4.84742 12 6.5C12 8.15258 11.4085 9.57042 10.2254 10.7535C9.06103 11.9178 7.65258 12.5 6 12.5C4.34742 12.5 2.92958 11.9178 1.74648 10.7535C0.58216 9.57042 0 8.15258 0 6.5C0 4.84742 0.58216 3.43897 1.74648 2.27465Z"
                    fill="white" />
                </svg>
                <div className="ai-credits-popup" style={{
                  width: 200,
                  fontSize: 12,
                  fontStyle: 'normal',
                  fontWeight: 400,
                  letterSpacing: 0.3,
                  lineHeight: '18px',
                  marginLeft: '-5px',
                  marginRight: '-5px',
                  zIndex: 99999999999
                }}>
                  <p style={{ margin: 0, marginBottom: 10 }}>
                    AI summary, BrieflyCreate Output or email follow up generation, will use 1 AI credit.
                    Transcriptions and transcript uploads are unlimited on your free plan.
                  </p>
                  <a onClick={() => navigate('/settings/subscriptions')}
                    style={{ fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>Upgrade now</a>
                </div>
              </div>

            </div> : ''}
          {!userData.error && !isProGivenForFree ?
            <div className="subscription-status badge" onClick={() => navigate(`/settings/subscriptions`)} style={{
              background: 'white',
              width: 'unset',
              color: '#F97238',
              padding: '0 5px',
              display: 'flex',
              alignItems: 'center',
              marginRight: 10,
              cursor: 'pointer'
            }}>
              <svg style={{ position: 'relative', marginRight: 3, marginLeft: 3 }} width="12" height="12"
                viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5.04894 1.42705C5.3483 0.505738 6.6517 0.50574 6.95106 1.42705L7.5716 3.33688C7.70547 3.7489 8.08943 4.02786 8.52265 4.02786H10.5308C11.4995 4.02786 11.9023 5.26748 11.1186 5.83688L9.49395 7.01722C9.14347 7.27187 8.99681 7.72323 9.13068 8.13525L9.75122 10.0451C10.0506 10.9664 8.9961 11.7325 8.21238 11.1631L6.58778 9.98278C6.2373 9.72813 5.7627 9.72814 5.41221 9.98278L3.78761 11.1631C3.0039 11.7325 1.94942 10.9664 2.24878 10.0451L2.86932 8.13526C3.00319 7.72323 2.85653 7.27186 2.50604 7.01722L0.881445 5.83688C0.0977311 5.26748 0.500508 4.02786 1.46923 4.02786H3.47735C3.91057 4.02786 4.29453 3.7489 4.4284 3.33688L5.04894 1.42705Z"
                  fill="#F97238" />
              </svg>
              {!(subscription_is_active || (isTeamMemberAccount && admin_user_subscription_is_active)) ? 'UPGRADE TO PRO' : isTeamAccount || isTeamMemberAccount ? 'TEAM' : 'PRO'}
            </div> : ''}
          <div className="user-dropdown">
            {userData?.email && <>
              <div className="label">
                {userData.email}
                <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0.576058 0.261882C0.260613 0.556031 0.25038 1.0526 0.553439 1.3595L6 6.875L11.4466 1.3595C11.7496 1.0526 11.7394 0.556032 11.4239 0.261883C11.119 -0.022429 10.6427 -0.010456 10.3525 0.288814L6 4.7769L1.64753 0.288814C1.35731 -0.0104558 0.880954 -0.0224294 0.576058 0.261882Z"
                    fill="white" />
                </svg>
              </div>

              <div className="user-dropdown-content" style={{ height: isBrieflySuccess() ? 60 : 215 }}>

                {isBrieflySuccess() ? <>

                </> : <>
                  <h6>Settings</h6>

                  <div onClick={() => navigate('/settings/ai-summary-email')} className="clickable">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M14.4225 5.01754V3.50877L8 7.26316L1.57746 3.50877V5.01754L8 8.73684L14.4225 5.01754ZM14.4225 2C14.8482 2 15.2113 2.15205 15.5117 2.45614C15.8372 2.76023 16 3.11111 16 3.50877V12.4912C16 12.8889 15.8372 13.2398 15.5117 13.5439C15.2113 13.848 14.8482 14 14.4225 14H1.57746C1.1518 14 0.776213 13.848 0.450704 13.5439C0.150235 13.2398 0 12.8889 0 12.4912V3.50877C0 3.11111 0.150235 2.76023 0.450704 2.45614C0.776213 2.15205 1.1518 2 1.57746 2H14.4225Z"
                        fill="white" />
                    </svg>
                    AI Summary Email
                  </div>

                  <div onClick={() => navigate('/settings/subscriptions')} className="clickable">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M8.53755 13.1822C8.20966 12.9732 7.79034 12.9732 7.46245 13.1822L5.07922 14.7015C4.32781 15.1805 3.37242 14.5116 3.56541 13.6417L4.23172 10.638C4.30912 10.2891 4.19441 9.92535 3.93084 9.68396L1.67237 7.61557C1.03133 7.02848 1.39294 5.95946 2.25874 5.88208L5.1377 5.62478C5.51141 5.59138 5.83499 5.35159 5.9757 5.00378L7.07299 2.29144C7.4101 1.45814 8.5899 1.45814 8.92701 2.29144L10.0243 5.00378C10.165 5.35159 10.4886 5.59138 10.8623 5.62478L13.7413 5.88208C14.6071 5.95946 14.9687 7.02848 14.3276 7.61557L12.0692 9.68396C11.8056 9.92535 11.6909 10.2891 11.7683 10.638L12.4346 13.6417C12.6276 14.5116 11.6722 15.1805 10.9208 14.7015L8.53755 13.1822Z"
                        fill="white" />
                    </svg>
                    Subscription
                  </div>


                  <div onClick={() => navigate('/settings/integrations')} className="clickable">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_1276_2192)">
                        <path fillRule="evenodd" clipRule="evenodd"
                          d="M3.70526 11.6645L1.81592 12.7553C1.32512 13.0387 0.697539 12.8705 0.414177 12.3797C0.130815 11.8889 0.298975 11.2613 0.789772 10.978L2.67912 9.88716C2.18856 8.12618 2.92626 6.18982 4.58259 5.23353L8.52605 2.95677C8.80213 2.79738 9.15514 2.89197 9.31453 3.16805L9.53897 3.55679L12.2049 2.01762C12.6957 1.73426 13.3233 1.90242 13.6066 2.39322C13.89 2.88401 13.7218 3.51159 13.231 3.79496L10.5651 5.33412L11.5912 7.11139L14.257 5.5723C14.7478 5.28893 15.3754 5.45709 15.6587 5.94789C15.9421 6.43869 15.774 7.06627 15.2832 7.34963L12.6174 8.88873L12.8419 9.27764C13.0013 9.55371 12.9067 9.90673 12.6306 10.0661L8.68717 12.3429C7.03083 13.2992 4.98503 12.9698 3.70526 11.6645Z"
                          fill="white" />
                      </g>
                      <defs>
                        <clipPath id="clip0_1276_2192">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    Integrations
                  </div>

                  <hr style={{ marginBottom: 14, marginTop: -7 }} />
                </>}


                <div className="clickable"
                  onClick={() => window.open('https://www.brieflyai.com/terms-of-service', '_blank')}
                  className="clickable">
                  Terms and Conditions
                </div>

                <div onClick={() => logOut(true)} className="clickable">
                  Log Out
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1.38028 2.24674V11.7533H6.31413C6.69292 11.7533 7 12.0603 7 12.4391V12.4391C7 12.8179 6.69292 13.125 6.31413 13.125H1.38028C1.00782 13.125 0.679186 12.9868 0.394366 12.7103C0.131455 12.4338 0 12.1148 0 11.7533V2.24674C0 1.8852 0.131455 1.56619 0.394366 1.28971C0.679186 1.01324 1.00782 0.875 1.38028 0.875H6.31413C6.69292 0.875 7 1.18208 7 1.56087V1.56087C7 1.93967 6.69292 2.24674 6.31413 2.24674H1.38028ZM10.0348 4.05416C10.3027 3.79405 10.7292 3.79505 10.9959 4.05641L13.7503 6.75535C13.8874 6.88965 13.8874 7.11034 13.7503 7.24465L11.0097 9.93012C10.7366 10.1977 10.2988 10.1951 10.0289 9.92438V9.92438C9.75473 9.64938 9.75839 9.20329 10.037 8.93281L11.338 7.66992H4.87649C4.50651 7.66992 4.20657 7.36999 4.20657 7V7C4.20657 6.63001 4.50651 6.33008 4.87649 6.33008H11.338L10.0303 5.0376C9.75598 4.76646 9.75798 4.32282 10.0348 4.05416V4.05416Z"
                      fill="white" />
                  </svg>
                </div>

              </div>
            </>}
          </div>
        </div>

      </div>

      {showRemoveMeetingPopupForMeeting ? <div className="transcript-info-popup-container" id="slack-tip-popup">
        <div className="popup-background" style={{}} onClick={() => setShowSlackTip(false)}></div>
        <div className="popup-content">

          <h1 style={{
            "color": "#FFF",
            "textAlign": "center",
            "fontFamily": "\"Source Sans Pro\"",
            "fontSize": "24px",
            "fontStyle": "normal",
            "fontWeight": "900",
            "lineHeight": "normal"
          }}>
            Are you sure you want to delete this call?
          </h1>

          {/*slack_integration_is_visible ? <>
            <h5>Briefly Customer Profiles</h5>
            <p>Automatically update your customer information in a centralized view</p>


            <h5>Pre-meeting AI Brief</h5>
            <p>Get a digest of meeting information delivered 15 minutes before your meeting</p>
          </> : */''}

          <p style={{
            marginTop: 20,
            marginBottom: 20,
            "color": "var(--text-text-primary, #FFF)",
            "textAlign": "center",
            "fontFeatureSettings": "'clig' off, 'liga' off",
            "fontFamily": "Roboto",
            "fontSize": "16px",
            "fontStyle": "normal",
            "fontWeight": "500",
            "lineHeight": "22px",
            "letterSpacing": "0.15px"
          }}>
            This action cannot be reversed and this call and its associated data will be permanently removed.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <button className="alt-color" onClick={() => {
              setShowRemoveMeetingPopupForMeeting(null)
            }} style={{
              fontSize: 16,
              padding: '1px 30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 10
            }}>
              Cancel
            </button>
            <button onClick={() => {
              const m = showRemoveMeetingPopupForMeeting
              if (m.isBackend)
                removeBackendMeeting(m.meetingId);
              else
                removeMeeting(m.meetingId)

              setShowRemoveMeetingPopupForMeeting(null)
            }} style={{
              fontSize: 16,
              padding: '1px 30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 10
            }}>
              Yes, delete this call
            </button>
          </div>

        </div>
      </div> : ''}

      {showSlackTip ? <div className="transcript-info-popup-container" id="slack-tip-popup">
        <div className="popup-background" style={{}} onClick={() => setShowSlackTip(false)}></div>
        <div className="popup-content">
          <div className="close-icon" onClick={() => {
            setShowSlackTip(false)
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.1946 0.805369C15.6394 1.25016 15.6394 1.97131 15.1946 2.41611L9.61074 8L15.1946 13.5839C15.6394 14.0287 15.6394 14.7498 15.1946 15.1946C14.7498 15.6394 14.0287 15.6394 13.5839 15.1946L8 9.61074L2.41611 15.1946C1.97132 15.6394 1.25016 15.6394 0.805369 15.1946C0.360576 14.7498 0.360576 14.0287 0.805369 13.5839L6.38926 8L0.80537 2.41611C0.360577 1.97132 0.360576 1.25016 0.805369 0.805369C1.25016 0.360576 1.97131 0.360576 2.41611 0.805369L8 6.38926L13.5839 0.80537C14.0287 0.360577 14.7498 0.360576 15.1946 0.805369Z"
                fill="white" />
            </svg>
          </div>

          <h1>📝 Get all your updates in one place!</h1>

          {/*slack_integration_is_visible ? <>
            <h5>Briefly Customer Profiles</h5>
            <p>Automatically update your customer information in a centralized view</p>


            <h5>Pre-meeting AI Brief</h5>
            <p>Get a digest of meeting information delivered 15 minutes before your meeting</p>
          </> : */''}

          <div className="gray-area" style={{ background: '#212121' }}>
            <h5 style={{ marginTop: 5 }}>📑 Post Call AI Summaries</h5>
            <ul style={{ marginBottom: 5 }}>
              <li><b>Find Them Easily</b>: Your summaries pop up in a private Slack channel or DM, just for you. Set
                it up on the next step.
              </li>
              <li><b>All the Good Stuff</b>: Everything you loved in the email summaries, now also in Slack (and still
                in the dashboard and extension).
              </li>
              <li><b>Your Choice</b>: Love getting summaries in Slack but don’t want duplicate in emails? You can turn
                off email notifications in settings.
              </li>
            </ul>
          </div>

          <button onClick={() => {
            setShowSlackTip(false);
            navigate('/settings/integrations/slack')
          }} style={{ fontSize: 16, padding: '1px 30px' }}>
            Manage Slack Integration
          </button>

        </div>
      </div> : ''}

      {showPaymentTip ? <div className="transcript-info-popup-container" id="payment-tip-popup">
        <div className="popup-background" style={{}} onClick={() => setShowPaymentTip(false)}></div>
        <div className="popup-content">
          <div className="close-icon" onClick={() => {
            localStorage.setItem(SHOW_PAYMENT_TIP_KEY, "1")
            setShowPaymentTip(false)
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.1946 0.805369C15.6394 1.25016 15.6394 1.97131 15.1946 2.41611L9.61074 8L15.1946 13.5839C15.6394 14.0287 15.6394 14.7498 15.1946 15.1946C14.7498 15.6394 14.0287 15.6394 13.5839 15.1946L8 9.61074L2.41611 15.1946C1.97132 15.6394 1.25016 15.6394 0.805369 15.1946C0.360576 14.7498 0.360576 14.0287 0.805369 13.5839L6.38926 8L0.80537 2.41611C0.360577 1.97132 0.360576 1.25016 0.805369 0.805369C1.25016 0.360576 1.97131 0.360576 2.41611 0.805369L8 6.38926L13.5839 0.80537C14.0287 0.360577 14.7498 0.360576 15.1946 0.805369Z"
                fill="white" />
            </svg>
          </div>

          <h1>Thank you for participating in the Briefly Beta</h1>

          <p>
            Briefly has been free for all users during our Beta period. Thank you so much for your feedback and
            testing our product. We are now rolling out paid plans for all users.
          </p>

          <p className="gray-text">
            You can still access Briefly for free, but are limited to 5 AI summaries/month and unlimited meeting
            transcripts. Choose a plan to access unlimited AI summaries.
          </p>

          <div className="plans row">
            <div className={"plan " + (paymentTipSelectedPlan === 'free' ? 'selected' : '')}
              onClick={() => setPaymentTipSelectedPlan('free')}>
              <h2>Basic Plan</h2>
              <p className="gray-text">Free</p>
              <p className="gray-text">5 AI summaries/month, unlimited transcriptions</p>
            </div>
            <div style={{ height: 100 }} className={"plan " + (paymentTipSelectedPlan === 'pro' ? 'selected' : '')}
              onClick={() => setPaymentTipSelectedPlan('pro')}>
              <h2>Pro Plan</h2>
              <p className="gray-text">$15/month</p>
              <p className="gray-text">Unlimited AI summaries</p>
            </div>
            <div className={"plan " + (paymentTipSelectedPlan === 'team' ? 'selected' : '')}
              onClick={() => setPaymentTipSelectedPlan('team')}>
              <h2>Team Plan</h2>
              <p className="gray-text">$15/month/user</p>
              <p className="gray-text">Unlimited AI Summaries, managed team billing</p>
            </div>
          </div>

          <button onClick={() => {
            setShowPaymentTip(false)
            localStorage.setItem(SHOW_PAYMENT_TIP_KEY, "1")
            if (paymentTipSelectedPlan !== 'free') {
              openCheckoutSessionInNewTab()
            }
          }} style={{
            width: '180px',
            fontSize: 16,
            height: 40,
            marginTop: 25,
            marginBottom: 25
          }}>
            Choose this plan
          </button>

          <p className="gray-text">
            <a onClick={() => window.open('https://www.brieflyai.com/pricing', '_blank')}>Click here</a> for a
            detailed breakdown or <a onClick={() => window.open('https://www.brieflyai.com/contact-us', '_blank')}>contact
              our support team</a> - we're here to help!
          </p>

          <p style={{ fontSize: 14 }}>
            Thank you for being an integral part of our journey. We can't wait to continue growing with you.
          </p>

        </div>
      </div> : ''}

      {showOutOfCreditsPopup ? <div className="transcript-info-popup-container out-of-credits">
        <div className="popup-background" onClick={() => setShowOutOfCreditsPopup(false)}></div>
        <div className="popup-content">
          <div className="close-icon" onClick={() => setShowOutOfCreditsPopup(false)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.1946 0.805369C15.6394 1.25016 15.6394 1.97131 15.1946 2.41611L9.61074 8L15.1946 13.5839C15.6394 14.0287 15.6394 14.7498 15.1946 15.1946C14.7498 15.6394 14.0287 15.6394 13.5839 15.1946L8 9.61074L2.41611 15.1946C1.97132 15.6394 1.25016 15.6394 0.805369 15.1946C0.360576 14.7498 0.360576 14.0287 0.805369 13.5839L6.38926 8L0.80537 2.41611C0.360577 1.97132 0.360576 1.25016 0.805369 0.805369C1.25016 0.360576 1.97131 0.360576 2.41611 0.805369L8 6.38926L13.5839 0.80537C14.0287 0.360577 14.7498 0.360576 15.1946 0.805369Z"
                fill="white" />
            </svg>
          </div>

          <svg width="145" height="108" viewBox="0 0 145 108" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd"
              d="M28.5161 25.2369H124.308C126.654 25.2369 128.556 27.1385 128.556 29.4842V95.7412C128.556 98.0869 126.654 99.9885 124.308 99.9885H28.5161C26.1705 99.9885 24.2689 98.0869 24.2689 95.7412V29.4842C24.2689 27.1385 26.1705 25.2369 28.5161 25.2369Z"
              fill="#2C2C2C" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M59.3706 79.16H93.9116C95.4481 79.16 96.6937 80.4056 96.6937 81.9421V88.7864C96.6937 90.3229 95.4481 91.5685 93.9116 91.5685H59.3706C57.8341 91.5685 56.5885 90.3229 56.5885 88.7864V81.9421C56.5885 80.4056 57.8341 79.16 59.3706 79.16Z"
              fill="#E44867" />
            <path d="M69.3069 85.3633H83.9717" stroke="#2C2C2C" strokeWidth="0.626126" strokeLinecap="round"
              strokeLinejoin="round" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M28.5161 25.2369H124.308C126.654 25.2369 128.556 27.1385 128.556 29.4842V95.7412C128.556 98.0869 126.654 99.9885 124.308 99.9885H28.5161C26.1705 99.9885 24.2689 98.0869 24.2689 95.7412V29.4842C24.2689 27.1385 26.1705 25.2369 28.5161 25.2369Z"
              stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M76.6402 33.6337H107.562" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M34.2823 33.6337H72.2416" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M34.2823 37.5564H99.7673" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M34.2823 41.4809H58.4209" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M62.8266 41.4809H86.041" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M42.5968 60.0002H104.449" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <ellipse cx="41.3289" cy="59.9789" rx="7.04481" ry="7.0377" fill="#C6C6C6" />
            <path d="M38.1983 59.9344L41.1757 62.3503L44.4577 58.2906" stroke="#2C2C2C" strokeWidth="0.626126"
              strokeLinecap="round" strokeLinejoin="round" />
            <ellipse cx="111.494" cy="59.9789" rx="7.04481" ry="7.0377" fill="#434343" />
            <ellipse cx="76.4105" cy="59.9789" rx="7.04481" ry="7.0377" fill="#434343" />
            <path d="M74.3875 57.9562L78.4352 61.9998" stroke="#C6C6C6" strokeWidth="0.626126" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M74.3875 61.9998L78.4352 57.9562" stroke="#C6C6C6" strokeWidth="0.626126" strokeLinecap="round"
              strokeLinejoin="round" />
            <ellipse cx="128.056" cy="104.729" rx="16.5278" ry="2.54739" fill="#2C2C2C" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M119.332 96.3401L136.508 96.6865L138.458 103.419C131.383 106.366 121.762 104.539 118.361 103.738L118.121 103.68L117.904 103.627C117.87 103.619 117.836 103.611 117.804 103.603L117.624 103.557L117.47 103.517L117.342 103.483L117.108 103.419L119.332 96.3401ZM125.443 76.8907C126.53 76.3472 127.803 76.3254 128.833 76.4498L128.96 76.4661C129.065 76.4802 129.168 76.4958 129.267 76.5124L129.384 76.5327L129.498 76.5538L129.608 76.5755L129.714 76.5977L129.817 76.6202L129.915 76.6429L130.009 76.6655L130.099 76.6879L130.225 76.7208L130.302 76.742L130.409 76.7724L130.503 76.8005L130.609 76.8335L130.673 76.8541L130.779 76.8907L135.093 91.8008L135.098 91.7992C131.69 93.133 126.061 92.9373 122.899 92.6878L122.63 92.6661L122.375 92.6442L122.133 92.6225L121.906 92.6012L121.694 92.5805L121.498 92.5606L121.235 92.5327L120.946 92.5003L120.692 92.4701L120.556 92.4528L125.443 76.8907Z"
              fill="#F97238" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M130.685 76.8581L130.779 76.8907L133.844 87.4833L133.661 87.5337C129.753 88.5895 126.127 88.4195 123.99 88.1521L123.794 88.1268C123.73 88.1183 123.668 88.1097 123.607 88.1012L123.429 88.0753L123.261 88.0495C123.234 88.0453 123.207 88.041 123.18 88.0367L123.027 88.0114L122.883 87.9867L122.749 87.9627L122.568 87.9286L122.46 87.9075L122.364 87.8878L122.278 87.8699L122.172 87.8466L122.027 87.813L122.014 87.8096L120.556 92.4528L120.692 92.4701L120.885 92.4933L121.081 92.5157L121.318 92.5416L121.593 92.5704C121.626 92.5737 121.659 92.577 121.693 92.5804L121.905 92.6012L122.132 92.6225C122.172 92.6261 122.211 92.6297 122.252 92.6333L122.5 92.6551L122.762 92.6769C125.895 92.9312 131.636 93.1523 135.094 91.8007L136.84 97.8319C130.984 99.6941 123.612 98.7172 120.437 98.1476L120.199 98.104C120.16 98.097 120.123 98.0899 120.086 98.083L119.874 98.0426L119.68 98.0047L119.506 97.9697L119.351 97.9378L119.157 97.8967L118.973 97.8563L118.866 97.8319L122.013 87.8095L122.011 87.809L123.825 82.0371L123.896 82.0564L123.986 82.0794L124.074 82.1012L124.181 82.1264L124.305 82.1544L124.397 82.1745L124.496 82.1954L124.657 82.2282L124.774 82.2506C124.794 82.2544 124.814 82.2582 124.835 82.262L124.961 82.285L125.094 82.3081L125.232 82.3311L125.377 82.354C125.402 82.3578 125.427 82.3615 125.452 82.3653L125.605 82.3876C127.195 82.6117 129.649 82.7478 132.137 82.0715L132.265 82.0362L130.775 76.8907L130.705 76.8663L130.58 76.8255L130.465 76.7902L130.351 76.757L130.221 76.7213L130.137 76.6989L130.016 76.6684L129.92 76.6453L129.82 76.6221L129.715 76.599L129.643 76.5838L129.569 76.5688L129.455 76.5467L129.337 76.5253L129.257 76.5115L129.175 76.4982L129.091 76.4854L128.964 76.4672L128.877 76.4559L128.789 76.4452L128.7 76.4353L128.61 76.4262C128.595 76.4247 128.58 76.4233 128.565 76.4219L128.473 76.414L128.381 76.4071L128.287 76.4011L128.193 76.3961L128.098 76.3922C128.05 76.3906 128.002 76.3893 127.954 76.3885L127.808 76.3875C127.923 76.3871 128.037 76.3894 128.149 76.394L128.245 76.3985C128.261 76.3993 128.277 76.4002 128.293 76.4011L128.387 76.4072C128.403 76.4083 128.418 76.4094 128.434 76.4106L128.527 76.4181C128.558 76.4208 128.589 76.4236 128.619 76.4266L128.71 76.4359L128.8 76.446C128.815 76.4477 128.83 76.4495 128.845 76.4513L128.933 76.4625L129.02 76.4743L129.106 76.4868L129.19 76.4998L129.272 76.5134L129.394 76.5345L129.511 76.5564L129.587 76.5714L129.662 76.5867L129.735 76.6021L129.84 76.6255L129.941 76.6489L130.068 76.6801L130.186 76.7106L130.296 76.7402L130.418 76.7751L130.562 76.8186L130.685 76.8581ZM130.779 76.8907L135.094 91.8004L135.093 91.8009L130.779 76.8907Z"
              fill="#2C2C2C" />
            <ellipse cx="128.147" cy="77.1244" rx="2.72876" ry="0.736928" fill="#102030" />
            <path d="M117.108 103.415L125.417 77.1244" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M138.458 103.415L130.876 77.1244" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M117.108 103.415L125.417 77.1244" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <path d="M138.458 103.415L130.876 77.1244" stroke="#C6C6C6" strokeWidth="0.417417" strokeLinecap="round"
              strokeLinejoin="round" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M63.7387 0.397081C63.7387 0.397081 26.2986 -1.21382 6.68312 2.05363C6.68312 2.05363 2.5243 3.31551 1.93364 9.07718L0.42207 40.122C0.42207 40.122 -0.678715 44.4473 6.54888 45.2232C6.54888 45.2232 34.6216 46.7133 44.7434 45.3226C44.7434 45.3226 49.697 44.0983 50.4407 37.0962L51.3401 11.9822L63.4622 2.65772C63.4622 2.65772 66.5014 0.49642 63.7387 0.397081Z"
              fill="#141414" stroke="#C6C6C6" strokeWidth="0.5" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M14.5041 32.5937C15.6488 32.5937 16.5768 33.5217 16.5768 34.6664C16.5768 35.8111 15.6488 36.7391 14.5041 36.7391C13.3593 36.7391 12.4314 35.8111 12.4314 34.6664C12.4314 33.5217 13.3593 32.5937 14.5041 32.5937Z"
              fill="#C6C6C6" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M26.218 31.1681C27.6756 31.1681 28.8572 32.3497 28.8572 33.8073C28.8572 35.2649 27.6756 36.4465 26.218 36.4465C24.7604 36.4465 23.5788 35.2649 23.5788 33.8073C23.5788 32.3497 24.7604 31.1681 26.218 31.1681Z"
              fill="#F97238" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M37.0648 30.3787C38.2169 30.3787 39.1509 31.3127 39.1509 32.4648C39.1509 33.617 38.2169 34.551 37.0648 34.551C35.9127 34.551 34.9787 33.617 34.9787 32.4648C34.9787 31.3127 35.9127 30.3787 37.0648 30.3787Z"
              fill="#C6C6C6" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M12.5334 15.9423L14.8853 28.5074L11.8031 29.3853L7.10464 18.2325L12.5334 15.9423Z"
              fill="#C6C6C6" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M38.7965 14.4468L45.3073 15.9289L40.3725 27.1649L36.9064 26.3971L38.7965 14.4468Z"
              fill="#C6C6C6" />
            <path fillRule="evenodd" clipRule="evenodd"
              d="M29.6036 8.26099C29.6834 9.31283 29.6169 11.0474 29.4774 13.0192L29.4452 13.4612C29.4341 13.6097 29.4226 13.7593 29.4108 13.9098L29.3743 14.3641C29.3681 14.4402 29.3618 14.5166 29.3554 14.593L29.3162 15.0538L29.2754 15.5174L29.2332 15.9829L29.1897 16.4491L29.1451 16.9151L29.0767 17.6109L29.0303 18.0713L28.9599 18.7537L28.8891 19.4231L28.8185 20.076L28.7487 20.7085L28.6579 21.514L28.5501 22.4477L28.4513 23.2843L28.3187 24.3786L28.1887 25.4171L24.5158 25.9971C23.0445 22.943 22.131 19.0138 21.5853 15.848L21.5216 15.4719L21.4613 15.1037C21.4516 15.0431 21.442 14.9828 21.4325 14.9228L21.3773 14.5678L21.3253 14.2223L21.2766 13.8869L21.231 13.5624L21.1884 13.2492C21.1816 13.198 21.1748 13.1473 21.1682 13.0971L21.1122 12.6597L21.0783 12.3846L21.0473 12.1234L21.0189 11.8768L20.9813 11.5356L20.9593 11.3282L20.9149 10.8844L20.8896 10.6113L20.8618 10.28L29.6036 8.26099Z"
              fill="#F97238" />
          </svg>

          <h1>You don't have any AI credits left for this month...</h1>

          <p>
            You can wait until the end of the month to receive an additional 5 credits, or you can upgrade your
            membership to Briefly PRO
          </p>

          <button onClick={() => {
            navigate('/settings/subscriptions');
            setShowOutOfCreditsPopup(false)
          }}>
            Manage subscription
          </button>

        </div>
      </div> : ''}

      {showZoomOnboardingPopup ? <ZoomOnboardingPopup onClose={() => setShowZoomOnboardingPopup(false)} /> : ''}

      {showZoomTranscriptInfoPopup ? <div className="transcript-info-popup-container">
        <div className="popup-background" onClick={() => setShowZoomTranscriptInfoPopup(false)}></div>
        <div className="popup-content">
          <div className="close-icon" onClick={() => setShowZoomTranscriptInfoPopup(false)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.1946 0.805369C15.6394 1.25016 15.6394 1.97131 15.1946 2.41611L9.61074 8L15.1946 13.5839C15.6394 14.0287 15.6394 14.7498 15.1946 15.1946C14.7498 15.6394 14.0287 15.6394 13.5839 15.1946L8 9.61074L2.41611 15.1946C1.97132 15.6394 1.25016 15.6394 0.805369 15.1946C0.360576 14.7498 0.360576 14.0287 0.805369 13.5839L6.38926 8L0.80537 2.41611C0.360577 1.97132 0.360576 1.25016 0.805369 0.805369C1.25016 0.360576 1.97131 0.360576 2.41611 0.805369L8 6.38926L13.5839 0.80537C14.0287 0.360577 14.7498 0.360576 15.1946 0.805369Z"
                fill="white" />
            </svg>
          </div>
          <h1>How to get a Zoom transcript</h1>
          <p>
            There are multiple ways to obtain a Zoom transcript. <b>Follow the instructions below to get transcripts
              that work best with BrieflyCreate.</b>
          </p>
          <h2>1 - Activate Closed Captioning</h2>
          <ul>
            <li>Go to your profile setting</li>
            <li>Click on In Meeting (Advanced)</li>
            <li>Scroll down and enable the following options</li>
            <li className="margin-left">Automated captions</li>
            <li className="margin-left">Full transcript</li>
            <li className="margin-left">Save Captions</li>
          </ul>
          <img src={ZoomStep1Png} />
          <h2>2 - Save transcripts from Zoom</h2>
          <ul>
            <li>During a Zoom call, click on "More" (represented by three dots) in the bottom bar and Select
              "Captions"
            </li>
          </ul>
          <img src={ZoomStep2Png} />
          <ul>
            <li>Choose “View Full Transcript”</li>
          </ul>
          <img src={ZoomStep3Png} />
          <ul>
            <li>Click on “Save Transcript”</li>
          </ul>
          <img src={ZoomStep4Png} />
          <h2>3 - For best results</h2>
          <ul>
            <li>Ensure that you upload a transcript that includes the participants' names.</li>
          </ul>
        </div>
      </div> : ''}

      {showMsTeamsTranscriptInfoPopup ? <div className="transcript-info-popup-container">
        <div className="popup-background" onClick={() => setShowMsTeamsTranscriptInfoPopup(false)}></div>
        <div className="popup-content">
          <div className="close-icon" onClick={() => setShowMsTeamsTranscriptInfoPopup(false)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.1946 0.805369C15.6394 1.25016 15.6394 1.97131 15.1946 2.41611L9.61074 8L15.1946 13.5839C15.6394 14.0287 15.6394 14.7498 15.1946 15.1946C14.7498 15.6394 14.0287 15.6394 13.5839 15.1946L8 9.61074L2.41611 15.1946C1.97132 15.6394 1.25016 15.6394 0.805369 15.1946C0.360576 14.7498 0.360576 14.0287 0.805369 13.5839L6.38926 8L0.80537 2.41611C0.360577 1.97132 0.360576 1.25016 0.805369 0.805369C1.25016 0.360576 1.97131 0.360576 2.41611 0.805369L8 6.38926L13.5839 0.80537C14.0287 0.360577 14.7498 0.360576 15.1946 0.805369Z"
                fill="white" />
            </svg>
          </div>
          <h1>How to get a MS Teams transcript</h1>
          <p>
            There are multiple ways to obtain a MS Teams transcript. <b>Follow the instructions below to get
              transcripts that work best with BrieflyCreate.</b>
          </p>
          <h2>1 - Start live transcription</h2>
          <ul>
            <li>Go to the meeting controls and select the "More actions" icon (represented by three dots).</li>
            <li>Choose "Start transcription."</li>
            <li>Participants will receive a notification, and the transcript will appear on the right side of the
              screen.
            </li>
          </ul>
          <img src={MsTeamsStep1Png} />
          <h2>2 - Save transcripts</h2>
          <ul>
            <li>After the meeting, the transcript is immediately available in the meeting event on the calendar.</li>
            <li>Select "Calendar" on the left in Teams.</li>
            <li>Open the meeting event and select the transcript.</li>
            <li>Click "Download" and choose .vtt</li>
          </ul>
          <img src={MsTeamsStep2Png} />

          <h2>3 - For best results</h2>
          <ul>
            <li>Ensure that you upload a transcript that includes the participants' names.</li>
          </ul>
        </div>
      </div> : ''}

      {showReferrerNotification ? <div id="briefly-notification" className={isReferrerSubmitSuccessful ? '' : 'error'}
        style={{ display: 'block', width: 400 }}>
        <div>
          <svg className="icon-not-error" width="16" height="16" viewBox="0 0 16 16" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6.38498 12.0188L13.5962 4.80751L12.4695 3.64319L6.38498 9.7277L3.53052 6.87324L2.40376 8L6.38498 12.0188ZM2.32864 2.3662C3.9061 0.788732 5.79656 0 8 0C10.2034 0 12.0814 0.788732 13.6338 2.3662C15.2113 3.91862 16 5.79656 16 8C16 10.2034 15.2113 12.0939 13.6338 13.6714C12.0814 15.2238 10.2034 16 8 16C5.79656 16 3.9061 15.2238 2.32864 13.6714C0.776213 12.0939 0 10.2034 0 8C0 5.79656 0.776213 3.91862 2.32864 2.3662Z"
              fill="white" />
          </svg>
          <svg className="icon-error" width="16" height="16" viewBox="0 0 16 16" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_1815_13184)">
              <path
                d="M8 8.78873C8.43561 8.78873 8.78873 8.43561 8.78873 8V4.76995C8.78873 4.33435 8.43561 3.98122 8 3.98122C7.5644 3.98122 7.21127 4.33435 7.21127 4.76995V8C7.21127 8.43561 7.5644 8.78873 8 8.78873ZM8 12.0188C8.43561 12.0188 8.78873 11.6657 8.78873 11.23V11.1925C8.78873 10.7569 8.43561 10.4038 8 10.4038C7.5644 10.4038 7.21127 10.7569 7.21127 11.1925V11.23C7.21127 11.6657 7.5644 12.0188 8 12.0188ZM2.32864 2.3662C3.9061 0.788732 5.79656 0 8 0C10.2034 0 12.0814 0.788732 13.6338 2.3662C15.2113 3.91862 16 5.79656 16 8C16 10.2034 15.2113 12.0939 13.6338 13.6714C12.0814 15.2238 10.2034 16 8 16C5.79656 16 3.9061 15.2238 2.32864 13.6714C0.776213 12.0939 0 10.2034 0 8C0 5.79656 0.776213 3.91862 2.32864 2.3662Z"
                fill="white" />
            </g>
            <defs>
              <clipPath id="clip0_1815_13184">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>

          <span id="notification-header" style={{ color: 'white' }}>
            {isReferrerSubmitSuccessful ? "Referral Under Review!" : "Referrals are available to new users only"}
          </span>
          <svg onClick={() => setShowReferrerNotification(false)} width="12" height="12" viewBox="0 0 12 12"
            fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M11.396 0.604027C11.7296 0.937622 11.7296 1.47849 11.396 1.81208L7.20805 6L11.396 10.1879C11.7296 10.5215 11.7296 11.0624 11.396 11.396V11.396C11.0624 11.7296 10.5215 11.7296 10.1879 11.396L6 7.20805L1.81208 11.396C1.47849 11.7296 0.937622 11.7296 0.604027 11.396V11.396C0.270432 11.0624 0.270432 10.5215 0.604027 10.1879L4.79195 6L0.604027 1.81208C0.270432 1.47849 0.270432 0.937622 0.604027 0.604027V0.604027C0.937622 0.270432 1.47849 0.270432 1.81208 0.604027L6 4.79195L10.1879 0.604027C10.5215 0.270432 11.0624 0.270432 11.396 0.604027V0.604027Z"
              fill="white" />
          </svg>
        </div>
        <p id="notification-text">
          {isReferrerSubmitSuccessful ? "We've received your referral request. After we verify the email you entered, both of you will receive rewards." : "The referral program is for first-time users only."}
        </p>
      </div> : ''}

      {navigator.platform.indexOf("Win32") != -1 ? <style>
        {`
          *::-webkit-scrollbar {
            width: 0px;
          }
        `}
      </style> : ''}

      {body}
    </div>
  );
}
